import * as Sentry from "@sentry/react";

/**
 * Inicializa Sentry para el monitoreo de errores
 * @param {Object} options - Opciones de configuración
 * @param {string} options.dsn - DSN de Sentry (obtenido de las variables de entorno)
 * @param {string} options.environment - Entorno de la aplicación (development, staging, production)
 * @param {string} options.release - Versión de la aplicación
 */
export const initSentry = ({ dsn, environment, release }) => {
  if (!dsn) {
    console.warn("Sentry DSN no configurado. El monitoreo de errores está deshabilitado.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      // Integración para capturar errores no manejados en la consola
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      // Integración para capturar errores de red
      Sentry.httpClientIntegration(),
      // Integración para capturar errores de GraphQL
      Sentry.graphqlClientIntegration(),
    ],
    // Captura de errores antes de que se muestren al usuario
    beforeSend(event, hint) {
      // Filtrar errores que no queremos reportar
      if (event.exception) {
        const error = hint.originalException || hint.syntheticException;
        
        // Ignorar errores de red específicos si es necesario
        if (error && error.message && error.message.includes("NetworkError")) {
          return null;
        }
      }
      return event;
    },
    // Configuración de contexto del usuario
    initialScope: {
      tags: {
        component: "admin-dashboard",
      },
    },
    // Ignorar errores específicos si es necesario
    ignoreErrors: [
      // Errores de navegador comunes que no son críticos
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
  });
};

/**
 * Configura el contexto del usuario en Sentry
 * @param {Object} user - Información del usuario (puede ser un token decodificado)
 * @param {string} user.id - ID del usuario
 * @param {string} user.email - Email del usuario
 * @param {string} user.username - Nombre de usuario
 * @param {string} user.sub - Subject del token (puede ser el ID)
 */
export const setSentryUser = (user) => {
  if (!user) {
    clearSentryUser();
    return;
  }

  Sentry.setUser({
    id: user.id || user.sub || user.userId,
    email: user.email,
    username: user.username || user.name,
  });
};

/**
 * Limpia el contexto del usuario en Sentry (útil para logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};
